import {
  Alert,
  Button,
  FileUpload,
  FormLabel,
  Modal,
  Radio,
  RadioList,
  Select,
  Switch,
  Text,
  Toast,
  useToast,
} from "@oysterhr/ui"
import { ReactNode, useMemo, useState } from "react"
import { Controller, useForm } from "react-hook-form"
import { useTranslation } from "react-i18next"
import { useFileUploadComponent } from "~/shared/hooks/useFileUploadComponent"
import { FileStatus } from "~/shared/hooks/useFileUploadComponent/utils"
import { AgreementStateEnum, AgreementTypeEnum } from "@oysterhr/schema"
import { useUploadAgreementMutation } from "../hooks/useUploadAgreementMutation"
import * as Yup from "yup"
import { yupResolver } from "@hookform/resolvers/yup"
import { FilePayload } from "~/shared/hooks/useFileUploadComponent/types"
import { AgreementDetailsFragment } from "../../../gql/graphql"

type UploadAgreementModalAfterEngagedProps = {
  isOpen: boolean
  closeModal: () => void
  agreements: AgreementDetailsFragment[]
  employmentId: string
}

const validationSchema = Yup.object({
  agreementType: Yup.string().required("The agreement type is required"),
  file: Yup.object({
    file: Yup.mixed().required("The agreement file is required").nullable(),
  }).required("The agreement file is required"),
})

interface UploadAgreementValues {
  agreementType?: AgreementTypeEnum
  agreementState: AgreementStateEnum
  sendNotifications: boolean
  file: FilePayload
}

export function UploadAgreementModalAfterEngaged({
  isOpen,
  closeModal,
  agreements,
  employmentId,
}: UploadAgreementModalAfterEngagedProps) {
  // Hooks
  const [t] = useTranslation("Agreements")
  const { handleChange, handleRemoveFile } = useFileUploadComponent({
    multiple: false,
  })
  const toast = useToast()
  // Local state
  const [formError, setFormError] = useState<ReactNode>()

  // GraphQL
  const [uploadAgreementMutation, { loading }] = useUploadAgreementMutation()

  // Form
  const { handleSubmit, control, watch, formState, reset } = useForm<UploadAgreementValues>({
    resolver: yupResolver(validationSchema),
    defaultValues: {
      agreementState: AgreementStateEnum.Signed,
      sendNotifications: false,
    },
  })

  const onSubmit = async (values: UploadAgreementValues) => {
    if (!selectedAgreement || !values.agreementType) {
      return
    }
    const response = await uploadAgreementMutation({
      variables: {
        input: {
          employmentId: employmentId,
          agreementId: selectedAgreement.id,
          agreementState: values.agreementState,
          fileSignedId: values.file.signedId ?? "",
          agreementType: values.agreementType,
          sendNotifications: values.sendNotifications,
        },
      },
    })

    const errors = response.data?.uploadAgreement?.errors

    if (errors && errors.length > 0) {
      setFormError(errors.join("\n"))
    } else {
      const agreementHumanized = t(`agreementTypeEnum.${values.agreementType}`, { ns: "Common" })
      toast({
        render: (
          <Toast variation="success">
            <Toast.Content>
              {t("uploadAgreementModal.successMessage", { agreementType: agreementHumanized })}
            </Toast.Content>
          </Toast>
        ),
      })
      closeModal()
      reset()
    }
  }

  const selectedAgreementType = watch("agreementType")

  const selectedAgreement = agreements.find(
    agreement => agreement.agreementType === selectedAgreementType,
  )

  const isReplacingAgreement = selectedAgreement?.lastUpdate?.file

  const agreementTypeOptions = useMemo(() => {
    return agreements.map(agreement => ({
      label: t(`agreementTypeEnum.${agreement.agreementType}`, { ns: "Common" }),
      value: agreement.agreementType,
    }))
  }, [t, agreements])

  return (
    <Modal rootId="modal" isOpen={isOpen} onRequestClose={closeModal}>
      <Modal.Header>Upload agreement</Modal.Header>
      <Modal.Content>
        <form id="upload-agreement-form" onSubmit={handleSubmit(onSubmit)}>
          <div className="mb-4">
            <Controller
              name="agreementType"
              control={control}
              render={({ field: { name, onChange, value, onBlur }, fieldState: { error } }) => (
                <Select
                  label={<FormLabel label="Agreement type" />}
                  error={error?.message}
                  placeholder="Select the agreement type"
                  onChange={onChange}
                  name={name}
                  value={value}
                  onBlur={onBlur}
                >
                  {agreementTypeOptions.map(option => (
                    <Select.Option key={option.value} value={option.value}>
                      {option.label}
                    </Select.Option>
                  ))}
                </Select>
              )}
            />
          </div>
          {isReplacingAgreement && <AgreementWillBeReplacedAlert />}
          <div className="mb-6 space-y-1.5">
            <Controller
              control={control}
              name="agreementState"
              render={({ field: { value, ref: _, ...rest } }) => (
                <RadioList
                  {...rest}
                  label={<FormLabel label="Upload as" />}
                  orientation="horizontal"
                >
                  <Radio
                    id="Draft"
                    label="Draft"
                    value={AgreementStateEnum.Draft}
                    checked={value === AgreementStateEnum.Draft}
                    disabled
                  />
                  <Radio
                    id="Signed"
                    label="Signed"
                    value={AgreementStateEnum.Signed}
                    checked={value === AgreementStateEnum.Signed}
                  />
                </RadioList>
              )}
            />
            <span className="text-sm text-gray-400">
              Please make sure the data in the agreement matches the team member&apos;s data in the
              platform
            </span>
          </div>

          <div className="mb-6">
            <Controller
              name="file"
              control={control}
              render={({ field: { name, value, onChange } }) => {
                return (
                  <FileUpload
                    id={name}
                    name={name}
                    onChange={handleChange(([file]) => {
                      setFormError(undefined)
                      onChange(file)
                    })}
                    placeholder="Choose file(s)"
                    accept="application/pdf"
                    error={formState.errors["file"]?.file?.message}
                    label={
                      <FormLabel
                        label="Upload document"
                        htmlFor={name}
                        assistiveText={
                          <div className="flex flex-col text-sm font-normal leading-4 text-gray-400">
                            <span>Accepted file types: PDF</span>
                          </div>
                        }
                      />
                    }
                  >
                    {value && (
                      <FileUpload.Item
                        key={value.id}
                        href={value.href}
                        isLoading={[FileStatus.Idle, FileStatus.Uploading].includes(value.status)}
                        onRemove={() => {
                          handleRemoveFile(() => {
                            onChange(undefined)
                            setFormError(undefined)
                          })(value.id)
                        }}
                      >
                        {value.file.name}
                      </FileUpload.Item>
                    )}
                  </FileUpload>
                )
              }}
            />
            <div className="mt-6">
              <Controller
                name="sendNotifications"
                control={control}
                render={({ field: { value, onChange } }) => (
                  <Switch
                    label="Send email notification to customer?"
                    checked={value}
                    onChange={onChange}
                  />
                )}
              />
            </div>
          </div>

          {!!formError && (
            <Text size="small" className="mb-6 text-error-base">
              {formError}
            </Text>
          )}
        </form>
      </Modal.Content>
      <Modal.Footer>
        <Button variation="secondary" onClick={closeModal}>
          Cancel
        </Button>
        <Button form="upload-agreement-form" type="submit" isLoading={loading}>
          {isReplacingAgreement ? "Replace agreement" : "Add agreement"}
        </Button>
      </Modal.Footer>
    </Modal>
  )
}

const AgreementWillBeReplacedAlert = () => {
  return (
    <Alert variation="warning" className="mb-4">
      <Alert.Icon />
      <Alert.Heading>Replacing this agreement means:</Alert.Heading>
      <Alert.Content className="text-sm">
        <ul className="list-inside list-disc pl-2">
          <li>It will overwrite this agreement in the platform</li>
        </ul>
      </Alert.Content>
    </Alert>
  )
}
